import React, { useEffect, useState } from 'react';
import { Box, Typography, alpha } from '@mui/material';
import { motion, AnimatePresence } from 'framer-motion';

// ─── Dark Theme Palette ───────────────────────────────────────────────────
const D = {
  bg0: '#0f172a',
  bg1: '#111827',
  t100: '#f8fafc',
  t200: '#cbd5e1',
  t300: '#a78bfa',
};

// ─── Variant accent map ───────────────────────────────────────────────────
const ACCENTS = {
  default: {
    primary: '#38bdf8',
    secondary: '#3b82f6',
    tertiary: '#818cf8',
    badge: '#0f1f38',
    badgeBorder: '#38bdf8',
    badgeText: '#eff6ff',
    bar: '#1e3a8a',
    barFill: ['#38bdf8', '#3b82f6', '#818cf8'],
    blob1: '#38bdf8',
    blob2: '#3b82f6',
  },

  primary: {
    primary: '#60a5fa',
    secondary: '#3b82f6',
    tertiary: '#93c5fd',
    badge: '#0f1f38',
    badgeBorder: '#3b82f6',
    badgeText: '#eef2ff',
    bar: '#1e3a8a',
    barFill: ['#60a5fa', '#3b82f6', '#93c5fd'],
    blob1: '#3b82f6',
    blob2: '#60a5fa',
  },

  success: {
    primary: '#34d399',
    secondary: '#10b981',
    tertiary: '#6ee7b7',
    badge: '#052e2b',
    badgeBorder: '#10b981',
    badgeText: '#d1fae5',
    bar: '#064e3b',
    barFill: ['#34d399', '#10b981', '#6ee7b7'],
    blob1: '#10b981',
    blob2: '#34d399',
  },

  warning: {
    primary: '#93c5fd',
    secondary: '#60a5fa',
    tertiary: '#38bdf8',
    badge: '#172554',
    badgeBorder: '#60a5fa',
    badgeText: '#eff6ff',
    bar: '#1e3a8a',
    barFill: ['#93c5fd', '#60a5fa', '#38bdf8'],
    blob1: '#60a5fa',
    blob2: '#93c5fd',
  },

  error: {
    primary: '#818cf8',
    secondary: '#3b82f6',
    tertiary: '#38bdf8',
    badge: '#1e1b4b',
    badgeBorder: '#818cf8',
    badgeText: '#eef2ff',
    bar: '#312e81',
    barFill: ['#818cf8', '#3b82f6', '#38bdf8'],
    blob1: '#3b82f6',
    blob2: '#818cf8',
  },

  secondary: {
    primary: '#818cf8',
    secondary: '#60a5fa',
    tertiary: '#38bdf8',
    badge: '#172554',
    badgeBorder: '#60a5fa',
    badgeText: '#eff6ff',
    bar: '#1e3a8a',
    barFill: ['#818cf8', '#60a5fa', '#38bdf8'],
    blob1: '#60a5fa',
    blob2: '#38bdf8',
  },
};

// ─── CSS ──────────────────────────────────────────────────────────────────
const LOADING_CSS = `
  @keyframes ld-bounce-spin {
    0%   { transform: rotate(0deg) scale(1); }
    25%  { transform: rotate(90deg) scale(1.12); }
    50%  { transform: rotate(180deg) scale(1); }
    75%  { transform: rotate(270deg) scale(1.08); }
    100% { transform: rotate(360deg) scale(1); }
  }

  @keyframes ld-bounce-spin-rev {
    0%   { transform: rotate(0deg) scale(1); }
    25%  { transform: rotate(-90deg) scale(1.1); }
    50%  { transform: rotate(-180deg) scale(1); }
    75%  { transform: rotate(-270deg) scale(1.06); }
    100% { transform: rotate(-360deg) scale(1); }
  }

  @keyframes ld-blob-drift {
    0%,100% {
      border-radius: 60% 40% 55% 45% / 50% 60% 40% 55%;
      transform: rotate(0deg) scale(1);
    }

    33% {
      border-radius: 40% 60% 45% 55% / 60% 40% 60% 40%;
      transform: rotate(120deg) scale(1.06);
    }

    66% {
      border-radius: 55% 45% 60% 40% / 40% 55% 45% 60%;
      transform: rotate(240deg) scale(0.96);
    }
  }

  @keyframes ld-blob-drift-rev {
    0%,100% {
      border-radius: 55% 45% 60% 40% / 60% 40% 55% 45%;
      transform: rotate(0deg) scale(1);
    }

    33% {
      border-radius: 45% 55% 40% 60% / 40% 60% 45% 55%;
      transform: rotate(-120deg) scale(0.94);
    }

    66% {
      border-radius: 60% 40% 55% 45% / 55% 45% 60% 40%;
      transform: rotate(-240deg) scale(1.05);
    }
  }

  @keyframes ld-dot-wave {
    0%,100% { transform: translateY(0); }
    50% { transform: translateY(-10px); }
  }

  @keyframes ld-text-breathe {
    0%,100% { letter-spacing: 0.15em; }
    50% { letter-spacing: 0.28em; }
  }

  @keyframes ld-badge-float {
    0%,100% { transform: translateY(0); }
    50% { transform: translateY(-4px); }
  }

  @keyframes ld-orbit {
    from { transform: rotate(0deg) translateX(44px) rotate(0deg); }
    to   { transform: rotate(360deg) translateX(44px) rotate(-360deg); }
  }

  @keyframes ld-orbit2 {
    from { transform: rotate(180deg) translateX(30px) rotate(-180deg); }
    to   { transform: rotate(540deg) translateX(30px) rotate(-540deg); }
  }

  @keyframes ld-shimmer-bar {
    0% { transform: translateX(-100%); }
    100% { transform: translateX(300%); }
  }

  @keyframes ld-status-blink {
    0%,100% { opacity: 1; }
    50% { opacity: 0.3; }
  }
`;

if (
  typeof document !== 'undefined' &&
  !document.getElementById('utm-loading-css')
) {
  const el = document.createElement('style');
  el.id = 'utm-loading-css';
  el.textContent = LOADING_CSS;
  document.head.appendChild(el);
}

// ─── Animated Dots ────────────────────────────────────────────────────────
const useAnimatedDots = (active = true, ms = 480) => {
  const [n, setN] = useState(1);

  useEffect(() => {
    if (!active) return;

    const id = setInterval(() => {
      setN((d) => (d % 3) + 1);
    }, ms);

    return () => clearInterval(id);
  }, [active, ms]);

  return '.'.repeat(n);
};

// ─── Spinner ──────────────────────────────────────────────────────────────
const PlayfulSpinner = ({ size = 96, variant = 'default' }) => {
  const acc = ACCENTS[variant] || ACCENTS.default;

  const cx = size / 2;
  const rOuter = cx - size * 0.06;
  const rInner = cx - size * 0.22;

  const outerLen = 2 * Math.PI * rOuter;
  const innerLen = 2 * Math.PI * rInner;

  const sw = Math.max(2.5, size * 0.037);

  return (
    <Box
      sx={{
        position: 'relative',
        width: size,
        height: size,
        flexShrink: 0,
      }}
    >
      {/* Orbit Dot 1 */}
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          animation: 'ld-orbit 1.8s linear infinite',
        }}
      >
        <Box
          sx={{
            width: size * 0.115,
            height: size * 0.115,
            borderRadius: '50%',
            background: acc.primary,
            boxShadow: `0 2px 8px ${alpha(acc.primary, 0.5)}`,
          }}
        />
      </Box>

      {/* Orbit Dot 2 */}
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          animation: 'ld-orbit2 2.4s linear infinite',
        }}
      >
        <Box
          sx={{
            width: size * 0.085,
            height: size * 0.085,
            borderRadius: '50%',
            background: acc.secondary,
            boxShadow: `0 2px 6px ${alpha(acc.secondary, 0.45)}`,
          }}
        />
      </Box>

      {/* Rings */}
      <svg
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        style={{ position: 'absolute', inset: 0 }}
      >
        <circle
          cx={cx}
          cy={cx}
          r={rOuter}
          fill="none"
          stroke={acc.blob1}
          strokeWidth={sw}
          strokeOpacity={0.25}
        />

        <circle
          cx={cx}
          cy={cx}
          r={rOuter}
          fill="none"
          stroke={acc.primary}
          strokeWidth={sw}
          strokeLinecap="round"
          strokeDasharray={`${outerLen * 0.56} ${outerLen * 0.44}`}
          style={{
            transformOrigin: `${cx}px ${cx}px`,
            animation:
              'ld-bounce-spin 1.3s cubic-bezier(0.68,-0.55,0.27,1.55) infinite',
          }}
        />

        <circle
          cx={cx}
          cy={cx}
          r={rInner}
          fill="none"
          stroke={acc.blob2}
          strokeWidth={sw * 0.8}
          strokeOpacity={0.22}
        />

        <circle
          cx={cx}
          cy={cx}
          r={rInner}
          fill="none"
          stroke={acc.secondary}
          strokeWidth={sw * 0.85}
          strokeLinecap="round"
          strokeDasharray={`${innerLen * 0.42} ${innerLen * 0.58}`}
          style={{
            transformOrigin: `${cx}px ${cx}px`,
            animation:
              'ld-bounce-spin-rev 1.9s cubic-bezier(0.68,-0.55,0.27,1.55) infinite',
          }}
        />
      </svg>

      {/* Center Blob */}
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Box
          sx={{
            width: size * 0.23,
            height: size * 0.23,

            background: `
              linear-gradient(
                135deg,
                ${acc.primary},
                ${acc.secondary},
                ${acc.tertiary}
              )
            `,

            animation: 'ld-blob-drift 3s ease-in-out infinite',

            boxShadow: `0 4px 14px ${alpha(acc.primary, 0.45)}`,
          }}
        />
      </Box>
    </Box>
  );
};

// ─── Dot Wave ─────────────────────────────────────────────────────────────
const DotWave = ({ variant = 'default' }) => {
  const acc = ACCENTS[variant] || ACCENTS.default;

  const colors = [acc.primary, acc.secondary, acc.tertiary];

  return (
    <Box sx={{ display: 'flex', gap: '7px', alignItems: 'center' }}>
      {colors.map((color, i) => (
        <Box
          key={i}
          sx={{
            width: 9,
            height: 9,
            borderRadius: '50%',
            background: color,
            animation: `ld-dot-wave 1.2s ease-in-out ${
              i * 0.18
            }s infinite`,
          }}
        />
      ))}
    </Box>
  );
};

// ─── Status Badge ─────────────────────────────────────────────────────────
const StatusBadge = ({ message, variant }) => {
  const acc = ACCENTS[variant] || ACCENTS.default;

  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '8px',
        px: 1.75,
        py: 0.6,
        borderRadius: '99px',
        background: acc.badge,
        border: `1px solid ${acc.badgeBorder}`,
        animation: 'ld-badge-float 2.5s ease-in-out infinite',
      }}
    >
      <Box
        sx={{
          width: 6,
          height: 6,
          borderRadius: '50%',
          flexShrink: 0,
          background: acc.primary,
          animation: 'ld-status-blink 1.5s ease-in-out infinite',
        }}
      />

      <Typography
        sx={{
          fontFamily: 'monospace',
          fontSize: '0.6rem',
          fontWeight: 600,
          color: acc.badgeText,
          letterSpacing: '0.1em',
          textTransform: 'uppercase',
        }}
      >
        {message}
      </Typography>
    </Box>
  );
};

// ─── Progress Bar ─────────────────────────────────────────────────────────
const ProgressBar = ({ variant = 'default', progress = 0.5 }) => {
  const acc = ACCENTS[variant] || ACCENTS.default;

  const [p1, p2, p3] = acc.barFill;

  return (
    <Box
      sx={{
        width: '100%',
        maxWidth: 220,
        height: 5,
        background: acc.bar,
        borderRadius: '99px',
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          height: '100%',
          width: `${Math.min(100, Math.max(0, progress * 100))}%`,
          background: `linear-gradient(90deg, ${p1}, ${p2}, ${p3})`,
          borderRadius: '99px',
          position: 'relative',
          overflow: 'hidden',
          transition: 'width 0.4s ease',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            background:
              'linear-gradient(90deg, transparent, rgba(255,255,255,0.6), transparent)',
            animation: 'ld-shimmer-bar 1.5s ease-in-out infinite',
          }}
        />
      </Box>
    </Box>
  );
};

// ───────────────────────────────────────────────────────────────────────────
// Main Component
// ───────────────────────────────────────────────────────────────────────────
const Loading = ({
  message = 'Loading',
  size = 96,
  fullScreen = false,
  variant = 'default',
  statusText,
  animateDots = true,
  progress = 0.5,
}) => {
  const dots = useAnimatedDots(animateDots);
  const acc = ACCENTS[variant] || ACCENTS.default;

  const blobs = (
    <>
      <Box
        sx={{
          position: 'absolute',
          top: -40,
          left: -40,
          width: 120,
          height: 120,
          background: acc.blob1,
          opacity: 0.18,
          animation: 'ld-blob-drift 6s ease-in-out infinite',
          pointerEvents: 'none',
        }}
      />

      <Box
        sx={{
          position: 'absolute',
          bottom: -30,
          right: -30,
          width: 100,
          height: 100,
          background: acc.blob2,
          opacity: 0.16,
          animation: 'ld-blob-drift-rev 7.5s ease-in-out infinite',
          pointerEvents: 'none',
        }}
      />
    </>
  );

  const panel = (
    <Box
      sx={{
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 3.5,
        px: { xs: 4, sm: 5.5 },
        py: { xs: 4, sm: 5 },

        borderRadius: '22px',
        overflow: 'hidden',

        background: 'rgba(15, 23, 42, 0.72)',
        backdropFilter: 'blur(24px) saturate(180%)',

        border: `1px solid ${alpha(acc.primary, 0.25)}`,

        boxShadow: `
          0 8px 40px rgba(0,0,0,0.45),
          0 0 0 1px ${alpha(acc.primary, 0.08)} inset,
          0 2px 0 ${alpha(acc.secondary, 0.08)} inset
        `,
      }}
    >
      {blobs}

      <Box sx={{ position: 'relative', zIndex: 2 }}>
        <PlayfulSpinner size={size} variant={variant} />
      </Box>

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 1.5,
          position: 'relative',
          zIndex: 2,
        }}
      >
        <AnimatePresence mode="wait">
          <motion.div
            key={message}
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.26 }}
          >
            <Typography
              sx={{
                fontFamily: "'Syne', sans-serif",
                fontWeight: 700,
                fontSize: { xs: '1rem', sm: '1.1rem' },
                color: D.t100,
                letterSpacing: '0.15em',
                textTransform: 'uppercase',
                textAlign: 'center',
                animation: 'ld-text-breathe 2s ease-in-out infinite',
              }}
            >
              {message}

              <Box
                component="span"
                sx={{
                  color: acc.primary,
                  minWidth: '1.6ch',
                  display: 'inline-block',
                }}
              >
                {dots}
              </Box>
            </Typography>
          </motion.div>
        </AnimatePresence>

        <DotWave variant={variant} />

        {statusText && (
          <StatusBadge message={statusText} variant={variant} />
        )}

        <ProgressBar variant={variant} progress={progress} />
      </Box>
    </Box>
  );

  // ─── Fullscreen ──────────────────────────────────────────────────────
  if (fullScreen) {
    return (
      <Box
        sx={{
          position: 'fixed',
          inset: 0,
          zIndex: 9999,

          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',

          background: `
            radial-gradient(circle at top left, ${alpha(
              acc.primary,
              0.18
            )} 0%, transparent 40%),

            radial-gradient(circle at bottom right, ${alpha(
              acc.secondary,
              0.18
            )} 0%, transparent 40%),

            linear-gradient(
              160deg,
              #020617 0%,
              #0f172a 45%,
              #111827 100%
            )
          `,

          '&::before': {
            content: '""',
            position: 'absolute',
            inset: 0,
            zIndex: 0,
            pointerEvents: 'none',

            backgroundImage: `
              radial-gradient(${alpha(
                acc.primary,
                0.16
              )} 1px, transparent 1px)
            `,

            backgroundSize: '24px 24px',
          },
        }}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.88, y: 16 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{
            duration: 0.45,
            ease: [0.25, 0.46, 0.45, 0.94],
          }}
          style={{
            position: 'relative',
            zIndex: 1,
          }}
        >
          {panel}
        </motion.div>
      </Box>
    );
  }

  // ─── Inline ──────────────────────────────────────────────────────────
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, ease: 'easeOut' }}
      style={{
        display: 'flex',
        flex: 1,
        width: '100%',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',

          flex: 1,
          width: '100%',

          minHeight: 'calc(100vh - 130px)',
          py: 6,

          background: `
            linear-gradient(
              180deg,
              ${D.bg0} 0%,
              ${D.bg1} 100%
            )
          `,
        }}
      >
        {panel}
      </Box>
    </motion.div>
  );
};

export default Loading;
